import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Polyline } from "react-leaflet";
import L from "leaflet";
import { Zap, MapPin, CalendarDays, BedDouble, BatteryCharging, ArrowLeft, Car, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { CARTO_LIGHT } from "../lib/mapTiles";
import { useAuth } from "../context/AuthContext";
import { usePageMeta } from "../lib/seo";
import { Button } from "../components/ui/button";

export default function RouteTemplatePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/routes/${slug}`).then((r) => setT(r.data)).catch(() => setError(true));
  }, [slug]);
  usePageMeta(t ? `${t.name} — EV-roadtrip route | VoltRoute` : "Route | VoltRoute",
    t ? `${t.duration_days}-daagse EV-roadtrip van ${t.origin_name} naar ${t.destination_name} (${t.distance_km} km) inclusief overnachtingen en laadstops. Kopieer deze route naar je eigen auto.` : undefined);

  const copyRoute = async () => {
    if (!user) {
      toast.info("Maak gratis een account om deze route te kopiëren");
      navigate("/auth");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/routes/${slug}/copy`);
      sessionStorage.setItem("vr_wizard_prefill", JSON.stringify(data.prefill || {}));
      navigate("/nieuwe-trip");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4" data-testid="route-template-notfound">
        <p className="font-semibold">Deze route bestaat niet (meer)</p>
        <Link to="/routes" className="text-primary font-bold text-sm underline">Naar de route-bibliotheek</Link>
      </div>
    );
  }
  if (!t) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" /></div>;
  }
  const stays = (t.days_summary || []).filter((d) => d.stay_name);
  return (
    <div className="min-h-screen bg-background" data-testid="route-template-page">
      <header className="bg-[#1B263B] relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-4xl mx-auto px-5 py-10">
          <Link to="/routes" data-testid="back-to-library" className="text-[#74C69D] text-xs font-bold flex items-center gap-1.5 mb-6 w-fit hover:gap-2.5 transition-all duration-200">
            <ArrowLeft className="w-3.5 h-3.5" /> Route-bibliotheek
          </Link>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-white tracking-tight" data-testid="route-template-title">{t.name}</h1>
          <p className="text-white/70 mt-2 text-sm sm:text-base">{t.origin_name} → {t.destination_name}{t.round_trip ? " → thuis" : ""} · route van {t.owner_name}</p>
          <div className="flex flex-wrap gap-2 mt-5 text-xs font-semibold text-white">
            <span className="rounded-full bg-white/10 px-3 py-1.5 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#74C69D]" /> {t.duration_days} dagen</span>
            <span className="rounded-full bg-white/10 px-3 py-1.5 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#74C69D]" /> {t.distance_km} km</span>
            <span className="rounded-full bg-white/10 px-3 py-1.5 flex items-center gap-1.5"><BedDouble className="w-3.5 h-3.5 text-[#74C69D]" /> {stays.length} overnachtingen</span>
            {t.vehicle_label && <span className="rounded-full bg-white/10 px-3 py-1.5 flex items-center gap-1.5"><Car className="w-3.5 h-3.5 text-[#74C69D]" /> gereden met {t.vehicle_label}</span>}
          </div>
          <Button data-testid="copy-route-button" onClick={copyRoute} disabled={busy}
            className="mt-7 rounded-full bg-primary hover:bg-[#2D6A4F] text-primary-foreground font-bold px-6 h-11 active:scale-95 transition-[transform,background-color] duration-200">
            <Copy className="w-4 h-4 mr-2" /> {busy ? "Kopiëren..." : "Plan deze route met mijn auto"}
          </Button>
        </div>
      </header>

      {t.geometry?.length > 1 && (
        <div className="h-[40vh]" data-testid="route-template-map">
          <MapContainer bounds={L.latLngBounds(t.geometry)} boundsOptions={{ padding: [30, 30] }} scrollWheelZoom={false} className="w-full h-full">
            <TileLayer url={CARTO_LIGHT} attribution='&copy; OSM &copy; CARTO' />
            <Polyline positions={t.geometry} pathOptions={{ color: "#2D6A4F", weight: 5 }} />
          </MapContainer>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-5 py-10 space-y-8">
        <section className="bg-card rounded-2xl p-6" data-testid="route-template-days">
          <h2 className="font-heading font-bold text-lg mb-4">Dag per dag</h2>
          <div className="space-y-2.5">
            {(t.days_summary || []).map((d) => (
              <div key={d.day_number} className="flex items-center gap-3 text-sm">
                <span className="w-7 h-7 rounded-full bg-primary/10 text-primary font-mono font-bold text-xs flex items-center justify-center shrink-0">{d.day_number}</span>
                <span className="flex-1 min-w-0">
                  {d.type === "stay" ? `Verblijf in ${d.end_name}` : `${d.start_name} → ${d.end_name}`}
                  {d.stay_name && <span className="text-muted-foreground"> · {d.stay_name}</span>}
                </span>
                {d.type !== "stay" && (
                  <span className="font-mono text-xs text-muted-foreground shrink-0 flex items-center gap-2">
                    {d.charge_stops > 0 && <span className="flex items-center gap-0.5"><BatteryCharging className="w-3 h-3 text-primary" />{d.charge_stops}</span>}
                    {d.distance_km} km
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-primary rounded-2xl p-6 text-primary-foreground text-center" data-testid="route-template-cta">
          <h2 className="font-heading font-extrabold text-xl">Zelf deze route rijden?</h2>
          <p className="text-sm opacity-80 mt-1 mb-4">VoltRoute rekent de laadstops om naar jouw EV en jouw reisdata — gratis.</p>
          <Button data-testid="copy-route-button-bottom" onClick={copyRoute} disabled={busy}
            className="rounded-full bg-white text-primary hover:bg-white/90 font-bold px-6 active:scale-95 transition-[transform,background-color] duration-200">
            <Zap className="w-4 h-4 mr-2" /> Plan deze route met mijn auto
          </Button>
        </section>
      </main>
    </div>
  );
}
